"use client"

import Image from "next/image"
import { useRef } from "react"
import {
  AtlasAgentRun,
  AtlasInputBar,
  AtlasUserMessage,
  type AtlasChatStep,
} from "./AtlasChatMockup"
import { AtlasInlineOrb } from "./AtlasThinkingOrb"
import { useAtlasReveal } from "./useAtlasReveal"

const modelsRevealTargets = [
  { selector: ".models-copy", y: 32 },
  { selector: ".models-ui-mockup", y: 28, scale: 0.98 },
] as const

const principles = [
  {
    number: "01",
    title: "Не зависите от одной модели",
    text: "Задача не привязана к одному провайдеру: Atlas подбирает подходящие доступные модели под конкретный этап процесса.",
  },
  {
    number: "02",
    title: "Не используете максимум там, где он не нужен",
    text: "Для сбора данных не расходуются ресурсы топовых нейросетей, а для глубокого анализа привлекается специализированный интеллект.",
  },
  {
    number: "03",
    title: "Новые модели становятся частью системы",
    text: "Когда появляется новый инструмент, он встраивается в арсенал агента без необходимости покупать отдельную подписку.",
  },
] as const

const executionSteps: readonly AtlasChatStep[] = [
  {
    title: "Анализ внутренних данных.",
    detail: "Сравниваю расход, клики и конверсию кампаний за прошедший месяц.",
    source: "подключённые источники",
    time: "4.2 c",
    toolCalls: [
      {
        label: "Analytics",
        value: "Данные кампаний",
        icon: "database-linear",
      },
    ],
  },
  {
    title: "Внешняя аналитика.",
    detail:
      "Сверяю открытые материалы и замечаю внешние факторы, которые могли повлиять.",
    source: "web search",
    time: "12.8 c",
    toolCalls: [
      {
        label: "tavily_search",
        value: "открытые материалы по рынку",
        icon: "magnifer-linear",
      },
    ],
  },
  {
    title: "Стратегия и предложения.",
    detail: "Сопоставляю просадку внутренних метрик с рынком и собираю план гипотез.",
    source: "рекомендации",
    time: "6.1 c",
  },
] as const

type ModelMarqueeItem = {
  readonly name: string
  readonly logo: `/models/${string}.svg`
  readonly logoAlt: string
}

const modelMarqueeItems: readonly ModelMarqueeItem[] = [
  { name: "GLM 5.3 Flash", logo: "/models/z-ai.svg", logoAlt: "Z.ai" },
  { name: "DeepSeek V4 Pro 0813", logo: "/models/deepseek.svg", logoAlt: "DeepSeek" },
  { name: "Gemini 3.7 Flash", logo: "/models/gemini.svg", logoAlt: "Gemini" },
  { name: "GPT 5.4 Mini", logo: "/models/openai.svg", logoAlt: "OpenAI" },
  { name: "Claude Haiku 4.5", logo: "/models/claude.svg", logoAlt: "Claude" },
  { name: "Claude Sonnet 5", logo: "/models/claude.svg", logoAlt: "Claude" },
  { name: "GPT 5.4", logo: "/models/openai.svg", logoAlt: "OpenAI" },
  { name: "Claude Opus 5", logo: "/models/claude.svg", logoAlt: "Claude" },
  { name: "Gemini 3.1 Pro Preview", logo: "/models/gemini.svg", logoAlt: "Gemini" },
  { name: "DeepSeek V4 Flash", logo: "/models/deepseek.svg", logoAlt: "DeepSeek" },
] as const

function ModelMarqueeTrack({ ariaHidden = false }: { readonly ariaHidden?: boolean }) {
  return (
    <div
      aria-hidden={ariaHidden}
      className="flex min-w-max shrink-0 items-center gap-4 pr-4 sm:gap-5 sm:pr-5"
    >
      {modelMarqueeItems.map((item) => (
        <div
          key={item.name}
          className="models-marquee-item group flex min-w-max items-center gap-3 rounded-2xl border px-4 py-3 transition-transform duration-500 hover:-translate-y-1 sm:px-5"
        >
          <span className="models-marquee-logo flex size-9 shrink-0 items-center justify-center rounded-xl border transition-transform duration-500 group-hover:scale-110">
            <Image
              src={item.logo}
              alt={item.logoAlt}
              width={22}
              height={22}
              className="size-5 object-contain"
            />
          </span>
          <span className="models-marquee-name text-sm font-semibold sm:text-base">
            {item.name}
          </span>
        </div>
      ))}
    </div>
  )
}

function CampaignAnalysisCard() {
  return (
    <div className="atlas-result-card rounded-2xl border border-white/10 bg-white/[0.035] p-4 sm:ml-12 sm:p-5">
      <p className="font-mono text-2xs text-white/40">Результат Atlas</p>
      <h4 className="mt-1 text-base font-semibold text-white">
        Аналитика кампаний за месяц
      </h4>

      <div className="mt-4 grid gap-4">
        <div className="rounded-xl border border-white/10 bg-black/10 p-4">
          <p className="type-overline text-white/40">Выявленная просадка</p>
          <ul className="mt-3 space-y-2 text-xs text-white/75">
            <li>
              <span className="font-semibold text-white">Канал:</span> Таргетированная
              реклама показывает заметное снижение заявок.
            </li>
            <li>
              <span className="font-semibold text-white">Причина:</span> выросла
              конкуренция в нише, это видно по подключённым рекламным данным и открытым
              материалам.
            </li>
          </ul>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/10 p-4">
          <p className="type-overline text-white/40">Предложения на следующий месяц</p>
          <ol className="mt-3 space-y-2 pl-4 text-xs text-white/75">
            <li className="list-decimal">
              Перенести часть бюджета в поисковый контекст.
            </li>
            <li className="list-decimal">
              Запустить A/B тест новых офферов под просевший сегмент.
            </li>
          </ol>
        </div>
      </div>
    </div>
  )
}

export default function ModelsSection() {
  const sectionRef = useRef<HTMLElement>(null)

  useAtlasReveal(sectionRef, modelsRevealTargets)

  return (
    <section
      ref={sectionRef}
      id="models"
      aria-labelledby="models-heading"
      className="relative overflow-x-hidden border-y border-slate-200/70 bg-[#f7f6f1] py-20 dark:border-white/10 dark:bg-[#0d0f12] sm:py-28 lg:py-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-20 size-[32rem] rounded-full bg-orange-500/10 blur-3xl dark:bg-orange-500/15"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 bottom-10 size-[30rem] rounded-full bg-emerald-500/10 blur-3xl dark:bg-jungle-400/10"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-12">
          <div className="models-copy lg:col-span-5">
            <p className="type-eyebrow inline-flex items-center">
              <AtlasInlineOrb state="searching" className="ml-0" />
              Автовыбор интеллекта
            </p>
            <h2
              id="models-heading"
              className="mt-4 text-headline text-slate-950 dark:text-white"
            >
              Лучший ИИ для каждой задачи — автоматически.
            </h2>
            <p className="mt-5 text-lead text-slate-600 dark:text-slate-300">
              Разные этапы работы требуют разного инструментария. Atlas разделяет задачу и
              направляет её подходящим доступным исполнителям, сохраняя для вас интерфейс
              одного агента.
            </p>
            <div className="mt-8 space-y-6 border-t border-slate-200/80 pt-6 dark:border-white/10">
              {principles.map((p) => (
                <div key={p.number} className="flex gap-4">
                  <div className="type-numeric flex size-7 shrink-0 items-center justify-center rounded-lg bg-brand-500/10 text-xs text-brand-700 dark:bg-jungle-500/15 dark:text-jungle-300">
                    {p.number}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      {p.title}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      {p.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="models-board lg:col-span-7">
            <div className="atlas-chat-shell models-ui-mockup overflow-hidden rounded-2xl border border-slate-300/80 bg-[#16181d] text-slate-100 shadow-2xl dark:border-white/15 dark:bg-[#121418]">
              <div className="atlas-chat-header grid gap-3 border-b border-white/10 bg-[#1c1f26] px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-6">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#3b414b] text-2xs font-semibold text-white">
                    wJ
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-200 sm:text-xs">
                      Сравнение рекламных кампаний
                    </p>
                    <p className="mt-0.5 font-mono text-2xs text-slate-400">
                      Analytics · Web Search · Recommendations
                    </p>
                  </div>
                </div>

                <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-jungle-400 sm:px-2.5 sm:py-1 sm:text-2xs">
                  <AtlasInlineOrb state="solving" tone="success" className="ml-0 mr-1" />
                  Ход агента: Готово
                </span>
              </div>

              <div className="space-y-4 p-4 sm:p-6">
                <AtlasUserMessage>
                  Сравни результаты рекламных кампаний за месяц, найди просадки и
                  подготовь предложения на следующий месяц
                </AtlasUserMessage>
                <AtlasAgentRun
                  steps={executionSteps}
                  meta="3 шаг(а) · 2 вызов(а) · 12:45"
                />
                <CampaignAnalysisCard />
                <AtlasInputBar />
              </div>

              <div className="border-t border-white/5 bg-[#101216] px-4 py-2 text-center text-2xs text-slate-400">
                Atlas сверяет внутренние показатели с подключёнными источниками и
                возвращает конкретные предложения.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-14 sm:mt-16">
        <div className="models-marquee-shell relative left-1/2 w-screen -translate-x-1/2 border-y py-5 shadow-2xl sm:py-6">
          <div className="mx-auto max-w-[1800px]">
            <div className="animate-models-marquee flex w-max">
              <ModelMarqueeTrack />
              <ModelMarqueeTrack ariaHidden />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
